import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
      will-change: filter;
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['AWS', 'Azure', 'GCP', 'Splunk', 'RStudio', 'Kali Linux'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>I’m a security engineer who lives by one simple rule:</p>

            <p>I build secure systems because I’m a big fan of uninterrupted sleep.</p>
            
            <p>My journey started as a "sponge" at <a href="https://iahn.fpt.edu.vn/" target="_blank" rel="noopener noreferrer">a tech university</a> and grew
            through five years at <a href="https://fpt-is.com/en/" target="_blank" rel="noopener noreferrer">a leading regional systems integrator</a>. <a href="https://fpt-is.com/en/fpt-eagleeye/" target="_blank" rel="noopener noreferrer">My team</a> there
            taught me the real deal: security isn’t a bandage
            you slap on at the end—it’s an architectural choice. If it’s not secure by design from the first line
            of code, it’s just not finished.</p>

            <p>After building on five years in Vietnam, I moved to Australia to broaden my scope. I’m currently
              working toward <a href="https://adelaide.edu.au/study/degrees/master-of-information-technology-cyber-security/" target="_blank" rel="noopener noreferrer">my Master of IT</a>
              , fueled by plenty of coffee and collaborating with some brilliant minds on the 2026 frontiers of <a href="https://hoaio.com/" target="_blank" rel="noopener noreferrer">Quantum</a>, <a href="https://www.aisa.org.au/" target="_blank" rel="noopener noreferrer">Cloud Security</a>, and <a href="https://www.aicollective.com/" target="_blank" rel="noopener noreferrer">AI</a>.</p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.png"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
